'use client'
import * as React from 'react'
import Image from 'next/image'
import { ArrowUpRight, MapPin, Search, Users } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  partnerDirectory,
  PARTNER_REGIONS,
  PARTNER_SIZES,
  PARTNER_TYPES,
  PARTNER_SPECIALITIES,
  type PartnerDirectoryEntry,
} from '@/lib/copy/partner-directory'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'

type FilterState = {
  region?: string
  size?: string
  type?: string
  speciality?: string
}

export function PartnerDirectory() {
  const [query, setQuery] = React.useState('')
  const [filters, setFilters] = React.useState<FilterState>({})

  const filtered = partnerDirectory.filter((p) => {
    if (filters.region && p.region !== filters.region) return false
    if (filters.size && p.size !== filters.size) return false
    if (filters.type && !p.type.includes(filters.type as PartnerDirectoryEntry['type'][number])) return false
    if (filters.speciality && !p.specialities.includes(filters.speciality)) return false
    if (query) {
      const q = query.toLowerCase()
      if (!`${p.name} ${p.location} ${p.body} ${p.specialities.join(' ')}`.toLowerCase().includes(q)) return false
    }
    return true
  })

  function toggle<K extends keyof FilterState>(key: K, value: string) {
    setFilters((f) => ({ ...f, [key]: f[key] === value ? undefined : value }))
  }

  const activeCount = Object.values(filters).filter(Boolean).length

  return (
    <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
      <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search partners…"
            className="pl-10"
          />
        </div>
        <FilterGroup label="Location" options={PARTNER_REGIONS} active={filters.region} onToggle={(v) => toggle('region', v)} />
        <FilterGroup label="Employee count" options={PARTNER_SIZES} active={filters.size} onToggle={(v) => toggle('size', v)} />
        <FilterGroup label="Type" options={PARTNER_TYPES} active={filters.type} onToggle={(v) => toggle('type', v)} />
        <FilterGroup label="Specialities" options={PARTNER_SPECIALITIES} active={filters.speciality} onToggle={(v) => toggle('speciality', v)} compact />
        {(query || activeCount > 0) && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-4 transition hover:text-foreground"
            onClick={() => { setQuery(''); setFilters({}) }}
          >
            Reset all filters
          </button>
        )}
      </aside>

      <section>
        <div className="mb-5 flex items-center justify-between text-xs text-muted-foreground">
          <span>{filtered.length} of {partnerDirectory.length} partners</span>
          {activeCount > 0 && <span>{activeCount} filter{activeCount === 1 ? '' : 's'} active</span>}
        </div>

        {filtered.length > 0 ? (
          <ul className="grid gap-6 md:grid-cols-2">
            {filtered.map((p) => (
              <li key={p.slug}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col overflow-hidden rounded-md border bg-card transition hover:border-primary/40 hover:shadow-md"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <Image
                      src={unsplashPlaceholderFor(p.slug, { category: p.imageCategory, width: 1200, height: 675 })}
                      alt={p.name}
                      fill
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      className="object-cover transition duration-500 group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                    <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
                      <span className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-foreground shadow-sm">{p.type[0]}</span>
                      <span className="rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">{p.region}</span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 text-white">
                      <div>
                        <div className="font-display text-xl font-medium drop-shadow">{p.name}</div>
                        <div className="mt-0.5 flex items-center gap-3 text-[11px] opacity-90">
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{p.size}</span>
                        </div>
                      </div>
                      <ArrowUpRight className="h-5 w-5 opacity-90 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="text-sm text-muted-foreground">{p.body}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {p.specialities.map((s) => (
                        <span key={s} className="rounded-full border bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{s}</span>
                      ))}
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
            No partners match your filters. Reset and try again, or drop your requirements at <a href="mailto:partners@idukki.io" className="text-primary underline underline-offset-4">partners@idukki.io</a>.
          </div>
        )}
      </section>
    </div>
  )
}

function FilterGroup({ label, options, active, onToggle, compact }: { label: string; options: readonly string[]; active?: string; onToggle: (v: string) => void; compact?: boolean }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('mt-2 flex flex-wrap gap-1.5', compact && 'max-h-44 overflow-y-auto pr-1')}>
        {options.map((opt) => (
          <button
            type="button"
            key={opt}
            onClick={() => onToggle(opt)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs transition',
              active === opt ? 'border-primary bg-primary/10 text-primary' : 'border-input bg-card hover:border-primary/40 hover:text-foreground',
            )}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}
